import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ShieldCheck, Sparkles, Globe2, Star } from 'lucide-react';
import { ROLE_LANDINGS } from '../config/roleLandings';
import { useSEO, buildFaqSchema, buildBreadcrumbSchema, buildServiceSchema } from '../utils/seo';
import Header from '../components/Header';
import TrustStrip from '../components/TrustStrip';
import PublicTrustStrip from '../components/PublicTrustStrip';
import PulseReportCTA from '../components/PulseReportCTA';
import StickyMobileCTA from '../components/StickyMobileCTA';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const BASE_URL = 'https://shopperden.com';

/**
 * One SPA route, six SEO landing pages — `/for/:slug`.
 *
 * Indexable by Google: useSEO injects title, description, OG tags,
 * canonical, BreadcrumbList, FAQPage and Service schema for every variant.
 * Live counts come from /api/stats so content stays fresh on every crawl.
 */
const RoleLandingPage = () => {
  const { slug } = useParams();
  const cfg = ROLE_LANDINGS[slug];
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/stats`);
        if (!cancelled) setStats(res.data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const fillTokens = (tpl) => (tpl || '')
    .replace('{properties}', (stats?.properties || 1000).toLocaleString('en-GB'))
    .replace('{users}', (stats?.users || 100).toLocaleString('en-GB'))
    .replace('{countries}', String(stats?.countries || 3));

  const canonical = `${BASE_URL}/for/${cfg?.slug || ''}`;
  const description = cfg ? fillTokens(cfg.sub) : '';

  useSEO({
    title: cfg ? `${cfg.h1}` : 'Shopperden',
    description,
    canonical,
    type: 'website',
    image: `${BASE_URL}/icon-512.svg`,
    schema: cfg ? [
      buildBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: cfg.eyebrow, url: `/for/${cfg.slug}` },
      ]),
      buildFaqSchema(cfg.faqs),
      buildServiceSchema({
        name: `Shopperden ${cfg.eyebrow}`,
        description,
        url: canonical,
      }),
    ] : null,
  });

  if (!cfg) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white" data-testid={`role-landing-${cfg.slug}`}>
      <Header />
      <PublicTrustStrip
        pros={561}
        cities={stats?.countries ? stats.countries * 7 : 23}
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#FEF8EC] via-white to-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <p className="text-[10px] sm:text-xs tracking-[0.3em] text-[#E07A3A] uppercase font-bold mb-3" data-testid="role-landing-eyebrow">
            {cfg.eyebrow}
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-[#0F172A] leading-tight max-w-3xl" data-testid="role-landing-h1">
            {cfg.h1}
          </h1>
          <p className="text-base sm:text-lg text-slate-600 mt-5 max-w-2xl leading-relaxed">
            {fillTokens(cfg.sub)}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-7">
            <Link
              to={cfg.cta_link}
              className="inline-flex items-center gap-2 bg-[#0F172A] hover:bg-slate-800 text-white text-sm tracking-[0.2em] font-bold uppercase px-6 py-3 transition-colors"
              data-testid="role-landing-primary-cta"
            >
              {cfg.cta_label}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/find-a-professional"
              className="inline-flex items-center gap-2 border border-slate-200 hover:border-slate-300 text-[#0F172A] text-sm tracking-[0.2em] font-bold uppercase px-6 py-3 transition-colors"
              data-testid="role-landing-secondary-cta"
            >
              Browse local pros
            </Link>
          </div>
          <div className="mt-6">
            <TrustStrip />
          </div>
        </div>
      </section>

      {/* Bullets */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-testid="role-landing-bullets">
          {cfg.bullets.map((b, i) => (
            <div key={i} className="flex items-start gap-3 border border-slate-200 rounded p-4 bg-white">
              <ShieldCheck className="h-5 w-5 text-[#E07A3A] mt-0.5 flex-shrink-0" />
              <p className="text-sm text-[#0F172A] leading-relaxed">{fillTokens(b)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why us */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <p className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-bold mb-3">Why Shopperden</p>
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#0F172A] mb-8 leading-tight max-w-2xl">
            The fastest, fairest property network in {stats?.countries || 3} countries
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="role-landing-why-grid">
            {cfg.why_us.map((w, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded p-5">
                <Sparkles className="h-5 w-5 text-[#E07A3A] mb-3" />
                <h3 className="font-semibold text-[#0F172A] mb-2">{w.h}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{w.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats strip — proof of life */}
      {stats && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="role-landing-stats">
            <Stat label="Active listings" value={stats.properties} />
            <Stat label="Countries" value={stats.countries} icon={<Globe2 className="h-4 w-4" />} />
            <Stat label="Verified users" value={stats.verified_users} icon={<ShieldCheck className="h-4 w-4" />} />
            <Stat label="AI matches sent" value={stats.instant_matches} icon={<Star className="h-4 w-4" />} />
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <p className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-bold mb-3">Frequently asked</p>
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#0F172A] mb-6">Questions {cfg.eyebrow.toLowerCase().replace('for ', '')} ask</h2>
          <div className="space-y-2" data-testid="role-landing-faqs">
            {cfg.faqs.map((f, i) => (
              <details
                key={i}
                className="border border-slate-200 rounded p-4 group open:bg-slate-50/60"
                data-testid={`role-faq-${i}`}
              >
                <summary className="cursor-pointer font-semibold text-[#0F172A] text-sm group-open:text-[#E07A3A]">
                  {f.q}
                </summary>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl mb-4">{cfg.h1}</h2>
          <p className="text-sm sm:text-base text-slate-300 max-w-xl mx-auto mb-7">{fillTokens(cfg.sub)}</p>
          <Link
            to={cfg.cta_link}
            className="inline-flex items-center gap-2 bg-[#E07A3A] hover:bg-[#C4652E] text-white text-sm tracking-[0.2em] font-bold uppercase px-7 py-3.5 transition-colors"
            data-testid="role-landing-final-cta"
          >
            {cfg.cta_label}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <StickyMobileCTA
        label={`${cfg.cta_label} — flat fee · no commissions`}
        ctaText={cfg.cta_label.split(' ')[0]}
        to={cfg.cta_link}
        dataTestId={`sticky-cta-${cfg.slug}`}
      />
    </div>
  );
};

const Stat = ({ label, value, icon }) => (
  <div className="border border-slate-200 rounded p-4 bg-white">
    <div className="flex items-center gap-2 text-slate-500 text-[10px] uppercase tracking-wider mb-1">
      {icon}
      {label}
    </div>
    <p className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#0F172A]">
      {(value || 0).toLocaleString('en-GB')}
    </p>
  </div>
);

export default RoleLandingPage;
